import { db } from "@/api/firebase/firebase";
import {
  Timestamp,
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  writeBatch,
} from "firebase/firestore";

export type SnapshotPayload = Record<string, unknown>;
export type SnapshotViewMode = "default" | "custom";

export interface TransactionSnapshot {
  id: string; // composed id for uniqueness
  userId: string;
  cohortId: string;
  sessionId: string;
  stageIndex: number; // zero-based
  transactionId: number | null;
  // Which transaction set (primary/custom) this snapshot belongs to. Older
  // snapshots only carry it inside payload.viewMode — see snapshotViewMode.
  viewMode?: SnapshotViewMode;
  timestamp: Date;
  payload: SnapshotPayload;
}

const snapshotViewMode = (s: TransactionSnapshot): SnapshotViewMode =>
  (s.viewMode ?? (s.payload as any)?.viewMode) === "custom"
    ? "custom"
    : "default";

const COLLECTION_NAME = "transactionSnapshots";

/**
 * Drops snapshots whose transaction no longer exists in the cohort's
 * configured transaction list. Snapshot documents are written per transaction
 * as a participant progresses and are NOT deleted when an admin later removes
 * a transaction from the cohort, so stale ones would otherwise inflate the
 * exports (e.g. "18 transactions" when the cohort only has 17).
 *
 * When the configured list hasn't loaded yet (empty), the snapshots are
 * returned unfiltered rather than dropping everything.
 */
export const filterSnapshotsToTransactions = (
  snapshots: TransactionSnapshot[],
  configuredTransactionIds: Array<number | string>
): TransactionSnapshot[] => {
  if (!configuredTransactionIds.length) return snapshots;
  const validIds = new Set(
    configuredTransactionIds.map(Number).filter((n) => !isNaN(n))
  );
  return snapshots.filter((s) => {
    // Older snapshots may lack transactionId; they were written with a
    // zero-based stageIndex for transaction stageIndex + 1.
    const txId = Number(s.transactionId ?? (s.stageIndex ?? 0) + 1);
    return validIds.has(txId);
  });
};

/**
 * Recursively removes undefined values from an object.
 * Firestore doesn't accept undefined values.
 */
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === "object") {
    const cleaned: any = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    }
    return cleaned;
  }
  return obj;
}

export class SnapshotService {
  // Primary and custom transactions are both numbered from stage 0, so the
  // custom set gets its own document ids — otherwise saving custom
  // transaction #3 overwrites the primary #3 snapshot and vice versa. Primary
  // keeps the original ids so existing snapshots are still found.
  private static docId(
    userId: string,
    cohortId: string,
    sessionId: string,
    stageIndex: number,
    viewMode: SnapshotViewMode = "default"
  ) {
    const base = `${userId}_${cohortId}_${sessionId}_${stageIndex}`;
    return viewMode === "custom" ? `${base}_custom` : base;
  }

  /**
   * Merge account arrays, preserving names/labels/comments from existing if incoming doesn't have them.
   * This prevents race conditions where saving with empty values overwrites previously saved data.
   *
   * @param incoming - The new account data from current React state
   * @param existing - The existing account data from the same stageIndex snapshot
   * @param fallback - The account data from the previous stageIndex snapshot (used when existing has no data)
   */
  private static mergeAccountArrays(
    incoming: any[] = [],
    existing: any[] = [],
    fallback: any[] = []
  ): any[] {
    const existingById = new Map(existing.map((a) => [a.id, a]));
    const fallbackById = new Map(fallback.map((a) => [a.id, a]));

    return incoming.map((acc) => {
      const existingAcc = existingById.get(acc.id);
      const fallbackAcc = fallbackById.get(acc.id);

      // Preserve name: use incoming if non-empty, else existing, else fallback, else use incoming as-is
      // This prevents race conditions where an empty name overwrites a previously saved name
      const name =
        acc.name && acc.name.trim() !== ""
          ? acc.name
          : existingAcc?.name && existingAcc.name.trim() !== ""
          ? existingAcc.name
          : fallbackAcc?.name && fallbackAcc.name.trim() !== ""
          ? fallbackAcc.name
          : acc.name;

      // Build the merged account, only including label/comment if they have values
      const merged: any = {
        id: acc.id,
        name: name,
      };

      // Preserve label: use incoming if present and non-empty, else existing, else fallback, else omit
      // Note: We check for non-empty string because empty string would prevent fallback with ?? operator
      const label =
        acc.label && acc.label !== ""
          ? acc.label
          : existingAcc?.label && existingAcc.label !== ""
          ? existingAcc.label
          : fallbackAcc?.label && fallbackAcc.label !== ""
          ? fallbackAcc.label
          : null;
      if (label != null && label !== "") {
        merged.label = label;
      }

      // Preserve comment: use incoming if present and non-empty, else existing, else fallback, else omit
      const comment =
        acc.comment && acc.comment !== ""
          ? acc.comment
          : existingAcc?.comment && existingAcc.comment !== ""
          ? existingAcc.comment
          : fallbackAcc?.comment && fallbackAcc.comment !== ""
          ? fallbackAcc.comment
          : null;
      if (comment != null && comment !== "") {
        merged.comment = comment;
      }

      return merged;
    });
  }

  static async saveSnapshot(params: {
    userId: string;
    cohortId: string;
    sessionId: string;
    stageIndex: number;
    transactionId: number | null;
    payload: SnapshotPayload;
    viewMode?: SnapshotViewMode;
  }): Promise<void> {
    const { userId, cohortId, sessionId, stageIndex, transactionId, payload } =
      params;
    const viewMode = params.viewMode ?? "default";

    const id = this.docId(userId, cohortId, sessionId, stageIndex, viewMode);
    const ref = doc(db, COLLECTION_NAME, id);

    // Read existing snapshot to merge labels properly
    const existingSnap = await getDoc(ref);
    const existingPayload = existingSnap.exists()
      ? (existingSnap.data()?.payload as any) || {}
      : {};
    const existingAccounts = existingPayload?.accounts || {};

    // Also read the previous stageIndex snapshot as a fallback for names/labels
    // This fixes the issue where a new transaction's snapshot doesn't have account names
    // because the user hasn't saved them yet (onBlur hasn't fired)
    let fallbackAccounts: any = {};
    if (stageIndex > 0) {
      const prevId = this.docId(
        userId,
        cohortId,
        sessionId,
        stageIndex - 1,
        viewMode
      );
      const prevRef = doc(db, COLLECTION_NAME, prevId);
      const prevSnap = await getDoc(prevRef);
      if (prevSnap.exists()) {
        fallbackAccounts = (prevSnap.data()?.payload as any)?.accounts || {};
      }
    }

    // Get incoming accounts
    const incomingAccounts = (payload as any)?.accounts || {};

    // Merge each account type, preserving labels from existing if incoming doesn't have one
    // Use fallback from previous stageIndex if current stageIndex has no existing data
    const mergedAccounts = {
      assets: this.mergeAccountArrays(
        incomingAccounts.assets || [],
        existingAccounts.assets || [],
        fallbackAccounts.assets || []
      ),
      liabilities: this.mergeAccountArrays(
        incomingAccounts.liabilities || [],
        existingAccounts.liabilities || [],
        fallbackAccounts.liabilities || []
      ),
      equity: this.mergeAccountArrays(
        incomingAccounts.equity || [],
        existingAccounts.equity || [],
        fallbackAccounts.equity || []
      ),
      expenses: this.mergeAccountArrays(
        incomingAccounts.expenses || [],
        existingAccounts.expenses || [],
        fallbackAccounts.expenses || []
      ),
      income: this.mergeAccountArrays(
        incomingAccounts.income || [],
        existingAccounts.income || [],
        fallbackAccounts.income || []
      ),
    };

    // Build merged payload
    const mergedPayload = {
      ...payload,
      accounts: mergedAccounts,
    };

    const assetsWithLabels = mergedAccounts.assets
      .filter((a: any) => a.label)
      .map((a: any) => ({ id: a.id, name: a.name, label: a.label }));
    const liabilitiesWithLabels = mergedAccounts.liabilities
      .filter((l: any) => l.label)
      .map((l: any) => ({ id: l.id, name: l.name, label: l.label }));
    const equityWithLabels = mergedAccounts.equity
      .filter((e: any) => e.label)
      .map((e: any) => ({ id: e.id, name: e.name, label: e.label }));
    const expensesWithLabels = mergedAccounts.expenses
      .filter((e: any) => e.label)
      .map((e: any) => ({ id: e.id, name: e.name, label: e.label }));
    const incomeWithLabels = mergedAccounts.income
      .filter((i: any) => i.label)
      .map((i: any) => ({ id: i.id, name: i.name, label: i.label }));

    // Clean the payload to remove any undefined values (Firestore doesn't accept them)
    const cleanedPayload = removeUndefined(mergedPayload);

    await setDoc(
      ref,
      {
        id,
        userId,
        cohortId,
        sessionId,
        stageIndex,
        transactionId,
        viewMode,
        payload: cleanedPayload,
        timestamp: Timestamp.now(),
      },
      { merge: true }
    );
  }

  static async getSnapshot(params: {
    userId: string;
    cohortId: string;
    sessionId: string;
    stageIndex: number;
    viewMode?: SnapshotViewMode;
  }): Promise<TransactionSnapshot | null> {
    const { userId, cohortId, sessionId, stageIndex, viewMode } = params;
    const id = this.docId(userId, cohortId, sessionId, stageIndex, viewMode);
    const ref = doc(db, COLLECTION_NAME, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      ...(data as any),
      timestamp: data.timestamp?.toDate?.() ?? new Date(),
    } as TransactionSnapshot;
  }

  static async getSnapshots(params: {
    userId: string;
    cohortId: string;
    sessionId: string;
  }): Promise<TransactionSnapshot[]> {
    const { userId, cohortId, sessionId } = params;
    // Query by userId field (required for Firestore security rules validation)
    // and filter by cohortId and sessionId
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId),
      where("cohortId", "==", cohortId),
      where("sessionId", "==", sessionId)
    );
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => {
      const data = d.data();
      return {
        ...(data as any),
        timestamp: (data as any).timestamp?.toDate?.() ?? new Date(),
      } as TransactionSnapshot;
    });
    // Ensure correct order by numeric stageIndex
    list.sort((a, b) => (a.stageIndex ?? 0) - (b.stageIndex ?? 0));
    return list;
  }

  /**
   * Get all snapshots for a user+cohort across all sessions (for cross-device access).
   * Merges snapshots from all sessions, keeping the most recent snapshot for each stageIndex.
   * This ensures users see all their transactions regardless of which device/session they were created on.
   *
   * Only snapshots for `viewMode` (primary by default) are returned, so the
   * primary and custom transaction sets never mix in summaries or exports.
   */
  static async getAllSnapshotsForUserCohort(params: {
    userId: string;
    cohortId: string;
    viewMode?: SnapshotViewMode;
  }): Promise<TransactionSnapshot[]> {
    const { userId, cohortId } = params;
    const viewMode = params.viewMode ?? "default";
    // Query by userId and cohortId fields (required for Firestore security rules validation)
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId),
      where("cohortId", "==", cohortId)
    );
    const snap = await getDocs(q);

    if (snap.empty) return [];

    // Parse all snapshots
    const allSnapshots: TransactionSnapshot[] = snap.docs
      .map((d) => {
        const data = d.data();
        return {
          ...(data as any),
          timestamp: (data as any).timestamp?.toDate?.() ?? new Date(),
        } as TransactionSnapshot;
      })
      .filter((s) => snapshotViewMode(s) === viewMode);

    // Group snapshots by stageIndex (transaction number)
    // For each stageIndex, keep the snapshot with the most recent timestamp
    const snapshotsByStageIndex = new Map<number, TransactionSnapshot>();

    allSnapshots.forEach((snapshot) => {
      const stageIndex = snapshot.stageIndex ?? 0;
      const existing = snapshotsByStageIndex.get(stageIndex);

      // If no existing snapshot for this stageIndex, or this one is more recent, use this one
      if (!existing || snapshot.timestamp > existing.timestamp) {
        snapshotsByStageIndex.set(stageIndex, snapshot);
      }
    });

    // Convert map to array and sort by stageIndex
    const mergedSnapshots = Array.from(snapshotsByStageIndex.values());
    mergedSnapshots.sort((a, b) => (a.stageIndex ?? 0) - (b.stageIndex ?? 0));

    return mergedSnapshots;
  }

  static async deleteSnapshots(params: {
    userId: string;
    cohortId: string;
    sessionId?: string; // when omitted, delete all sessions for this user+cohort
  }): Promise<number> {
    const { userId, cohortId, sessionId } = params;
    // Query by userId and cohortId fields (required for Firestore security rules validation)
    // Optionally filter by sessionId if provided
    let q;
    if (sessionId) {
      q = query(
        collection(db, COLLECTION_NAME),
        where("userId", "==", userId),
        where("cohortId", "==", cohortId),
        where("sessionId", "==", sessionId)
      );
    } else {
      q = query(
        collection(db, COLLECTION_NAME),
        where("userId", "==", userId),
        where("cohortId", "==", cohortId)
      );
    }
    const snap = await getDocs(q);
    if (snap.empty) return 0;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    return snap.size;
  }
}
